import { useEffect, useRef, useState } from 'react'

import { ToastService } from '../../../services/toast.service'
import type { MultiSelectOption } from '../../copSubscription/components/MultiSelectList'
import type { SelectOption } from '../../copSubscription/components/SearchableSelect'
import {
    type Customer,
    fetchCustomers,
    fetchProductCategories,
    fetchRoles,
} from '../service/userManagement.service'
import { USER_MANAGEMENT_DEFAULTS as D } from '../UserManagement.constants'

export type AssignUserTabState = {
    customerOptions: SelectOption[]
    roleOptions: SelectOption[]
    productOptions: MultiSelectOption[]
    loading: boolean
    error: boolean
    searchingCustomers: boolean
    loadingMoreCustomers: boolean
    hasMoreCustomers: boolean
    setCustomerSearchTerm: (term: string) => void
    loadMoreCustomers: () => void
}

function isLoggedInUserManagerFromSession(): boolean {
    try {
        const raw = sessionStorage.getItem(D.SESSION_KEY_CURRENT_USER)
        if (!raw) return false
        const user = JSON.parse(raw) as { isUserManager?: boolean }
        return user.isUserManager === true
    } catch {
        return false
    }
}

const DEFAULT_CUSTOMER: Customer = { id: 'NOSLA', name: 'No SLA' }

function mapCustomersToOptions(customers: Customer[], includeDefault = false): SelectOption[] {
    const customersWithoutDefault = includeDefault
        ? customers.filter(customer => !/^nosla$/i.test(customer.id))
        : customers
    const customersWithDefault = includeDefault ? [DEFAULT_CUSTOMER, ...customersWithoutDefault] : customers
    const mapped = customersWithDefault.map(c => ({ value: c.id, label: `${c.name} (${c.id})` }))
    const isNoSla = (option: SelectOption) => /^nosla$/i.test(option.value)
    const nosla = mapped.filter(isNoSla)
    const rest = mapped.filter(option => !isNoSla(option))
    return nosla.length > 0 ? [...nosla, ...rest] : mapped
}

export function useAssignUserTab(): AssignUserTabState {
    const [customerOptions, setCustomerOptions] = useState<SelectOption[]>([])
    const [roleOptions, setRoleOptions] = useState<SelectOption[]>([])
    const [productOptions, setProductOptions] = useState<MultiSelectOption[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [searchingCustomers, setSearchingCustomers] = useState(false)
    const [loadingMoreCustomers, setLoadingMoreCustomers] = useState(false)
    const [hasMoreCustomers, setHasMoreCustomers] = useState(true)
    const [customerSearchTerm, setCustomerSearchTerm] = useState('')
    const initialCustomersLoaded = useRef(false)
    const customerApiCount = useRef(0)

    // skip products when the logged-in user is a User Manager creating/editing a user
    const skipProducts = isLoggedInUserManagerFromSession()

    useEffect(() => {
        if ((window as unknown as { Granite?: { author?: unknown } }).Granite?.author) { setLoading(false); return }
        setLoading(true)
        Promise.all([fetchCustomers(undefined, 0), fetchRoles()])
            .then(([customers, roles]) => {
                customerApiCount.current = customers.length
                setCustomerOptions(mapCustomersToOptions(customers, true))
                setHasMoreCustomers(customers.length === 50)
                initialCustomersLoaded.current = true
                setRoleOptions(roles.map(r => ({ value: String(r.id), label: r.name })))
            })
            .catch(() => {
                setError(true)
                ToastService.error(D.TOAST_ERROR_LOAD_FORM_DATA)
            })
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => {
        if (!initialCustomersLoaded.current) return
        let cancelled = false
        const timer = setTimeout(async () => {
            setSearchingCustomers(true)
            const customers = await fetchCustomers(customerSearchTerm || undefined, 0)
            if (cancelled) return
            customerApiCount.current = customers.length
            setCustomerOptions(mapCustomersToOptions(customers, !customerSearchTerm))
            setHasMoreCustomers(customers.length === 50)
            setSearchingCustomers(false)
        }, 300)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [customerSearchTerm])

    const loadMoreCustomers = async () => {
        if (!hasMoreCustomers || loadingMoreCustomers || searchingCustomers) return
        setLoadingMoreCustomers(true)
        try {
            const customers = await fetchCustomers(customerSearchTerm || undefined, customerApiCount.current)
            const mapped = mapCustomersToOptions(customers)
            setCustomerOptions(previous => [...previous, ...mapped])
            customerApiCount.current += customers.length
            setHasMoreCustomers(customers.length === 50)
        } finally {
            setLoadingMoreCustomers(false)
        }
    }

    // fetch products separately — skipped entirely for admin/user-manager
    useEffect(() => {
        if (skipProducts || (window as unknown as { Granite?: { author?: unknown } }).Granite?.author) { setProductOptions([]); return }
        fetchProductCategories()
            .then(products => setProductOptions(products.map(p => ({
                value: String(p.id),
                label: p.businessUnitShortName ? `${p.businessUnitShortName}- ${p.name}` : p.name,
            }))))
            .catch(() => { /* non-fatal; product list stays empty */ })
    }, [skipProducts])

    return {
        customerOptions,
        roleOptions,
        productOptions,
        loading,
        error,
        searchingCustomers,
        loadingMoreCustomers,
        hasMoreCustomers,
        setCustomerSearchTerm,
        loadMoreCustomers,
    }
}
